import { useEffect, useState } from 'react'
import { InvestPaymentMethodType, InvestPaymentMethodTypes } from '../../../types/mf-execution.types'
import {
  isPaymentMethodValidForAmount,
  isUpiIntentPaymentMethod,
} from '../../../utils/mf-execution/paymentMethodSelection'

type UsePaymentMethodAutoSwitchParams = {
  amount: number
  paymentMethods: InvestPaymentMethodType[]
  selectedPaymentMethod: InvestPaymentMethodType | null
  handlePaymentMethodChange: (paymentMethod: InvestPaymentMethodType | null) => void
  /** Built from the default installed app; used when auto-selecting UPI after amount decreases. */
  firstUpiIntentMethod?: InvestPaymentMethodType | null
  /** From payment-details API; caps UPI intent (and UPI when intents are installed). */
  upiIntentMaximumAllowedAmount?: number | null
  hasInstalledUpiIntents?: boolean
  /** False while `installedUpiApps` is still `null` (not yet read from storage). */
  areInstalledUpiIntentsReady?: boolean
}

type UsePaymentMethodAutoSwitchReturn = {
  blockBecauseNotAvailablePaymentMethod: boolean
  isUserExplicitSelection: boolean
  setIsUserExplicitSelection: (value: boolean) => void
}

/**
 * Auto-switches payment method when amount exceeds the applicable limit (UPI intent max or per-method max).
 * Priority for auto-selection: UPI intent (when provided) / UPI > net banking (never mandate).
 */
export const usePaymentMethodAutoSwitchUpiIntents = ({
  amount,
  paymentMethods,
  selectedPaymentMethod,
  handlePaymentMethodChange,
  firstUpiIntentMethod = null,
  upiIntentMaximumAllowedAmount = null,
  hasInstalledUpiIntents = false,
  areInstalledUpiIntentsReady = true,
}: UsePaymentMethodAutoSwitchParams): UsePaymentMethodAutoSwitchReturn => {
  const [blockBecauseNotAvailablePaymentMethod, setBlockBecauseNotAvailablePaymentMethod] = useState(false)
  const [isUserExplicitSelection, setIsUserExplicitSelection] = useState(false)

  const amountLimitOptions = {
    upiIntentMaximumAllowedAmount,
    hasInstalledUpiIntents,
  }

  const isMethodValidForAmount = (
    paymentMethod: InvestPaymentMethodType,
    checkAmount: number = amount
  ) => isPaymentMethodValidForAmount(paymentMethod, checkAmount, amountLimitOptions)

  const getEligiblePaymentMethodsForAmount = (checkAmount: number = amount): InvestPaymentMethodType[] => {
    if (!paymentMethods) return []

    return paymentMethods.filter(
      (method) =>
        method?.type !== InvestPaymentMethodTypes.MANDATE && isMethodValidForAmount(method, checkAmount)
    )
  }

  /**
   * Picks the best method for `checkAmount`. When native has no UPI apps, net banking wins over API UPI.
   * When intents exist, eligible UPI is upgraded to `firstUpiIntentMethod` for order/OTP package name.
   */
  const findBestPaymentMethodForAmount = (checkAmount: number = amount): InvestPaymentMethodType | null => {
    const eligibleMethods = getEligiblePaymentMethodsForAmount(checkAmount)

    if (eligibleMethods?.length === 0) {
      return null
    }

    const netBankingMethod = eligibleMethods.find(
      (method) => method?.type === InvestPaymentMethodTypes.NET_BANKING
    )

    // Confirmed empty intent list — do not auto-select linked UPI on the amount screen.
    if (areInstalledUpiIntentsReady && !hasInstalledUpiIntents && netBankingMethod) {
      return netBankingMethod
    }

    const upiMethod = eligibleMethods.find((method) => method?.type === InvestPaymentMethodTypes.UPI)
    if (upiMethod) {
      if (
        firstUpiIntentMethod?.paymentMethodId &&
        isMethodValidForAmount(firstUpiIntentMethod, checkAmount)
      ) {
        return firstUpiIntentMethod
      }
      return upiMethod
    }

    if (netBankingMethod) {
      return netBankingMethod
    }

    return eligibleMethods?.[0] || null
  }

  /**
   * Reacts to amount changes: switches to net banking when over UPI intent limit, back to UPI intent
   * when the amount fits. Does not run until installed intents are hydrated (same guard as the button).
   */
  useEffect(() => {
    if (!areInstalledUpiIntentsReady || !paymentMethods || !selectedPaymentMethod || amount <= 0) {
      return
    }

    const isCurrentMethodValid = isMethodValidForAmount(selectedPaymentMethod)

    // Keep a valid UPI intent selection; avoid replacing it with plain API UPI from `findBest`.
    if (isUpiIntentPaymentMethod(selectedPaymentMethod) && isCurrentMethodValid) {
      setBlockBecauseNotAvailablePaymentMethod(false)
      return
    }

    if (isUserExplicitSelection) {
      setBlockBecauseNotAvailablePaymentMethod(!isCurrentMethodValid)
      return
    }

    // Respect net banking / mandate chosen on the payment-methods screen (do not “optimize” back to UPI).
    if (
      isCurrentMethodValid &&
      (selectedPaymentMethod?.type === InvestPaymentMethodTypes.NET_BANKING ||
        selectedPaymentMethod?.type === InvestPaymentMethodTypes.MANDATE)
    ) {
      setBlockBecauseNotAvailablePaymentMethod(false)
      return
    }

    if (!isCurrentMethodValid) {
      const bestPaymentMethod = findBestPaymentMethodForAmount()

      if (bestPaymentMethod) {
        setBlockBecauseNotAvailablePaymentMethod(false)
        setIsUserExplicitSelection(false)
        handlePaymentMethodChange(bestPaymentMethod)
      } else {
        setBlockBecauseNotAvailablePaymentMethod(true)
      }
      return
    }

    const bestPaymentMethod = findBestPaymentMethodForAmount()

    if (bestPaymentMethod && bestPaymentMethod?.paymentMethodId !== selectedPaymentMethod?.paymentMethodId) {
      setBlockBecauseNotAvailablePaymentMethod(false)
      handlePaymentMethodChange(bestPaymentMethod)
    } else {
      setBlockBecauseNotAvailablePaymentMethod(false)
    }
  }, [
    amount,
    isUserExplicitSelection,
    selectedPaymentMethod,
    paymentMethods,
    handlePaymentMethodChange,
    firstUpiIntentMethod,
    upiIntentMaximumAllowedAmount,
    hasInstalledUpiIntents,
    areInstalledUpiIntentsReady,
  ])

  return {
    blockBecauseNotAvailablePaymentMethod,
    isUserExplicitSelection,
    setIsUserExplicitSelection,
  }
}
